/**
 * Messages — conversation list page with search, filter chips (All / Buddies /
 * Favourite / Groups), and conversation items. Tapping a conversation
 * navigates to the Chat page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useMessages } from '@wlb/shared/context/MessageContext';
import { isAiConversationType, isGroupConversationType } from '@wlb/shared/constants';
import { ToastService } from '@wlb/shared/platform/notifier';
import { SearchIcon, MessagesIcon, TrashIcon, BlockIcon, PlusIcon } from '../components/ui/icons';
import { ListStatus } from '../components/ui/ListStatus';
import { PopoverMenu, type PopoverMenuItem } from '../components/ui/PopoverMenu';
import { ConfirmModal } from '../features/feed/ConfirmModal';
import { ConversationItem } from '../features/messages/ConversationItem';
import { ConversationSkeleton } from '../features/messages/ConversationSkeleton';
import { resolveAvatarSrc } from '../features/messages/avatarSource';
import type { Conversation } from '@wlb/shared/types/chat';
import '../features/messages/messages.css';

type MessageFilter = 'all' | 'buddies' | 'favourite' | 'groups';
type ConversationMenuKey = 'createGroup' | 'block' | 'delete';

export function Messages() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    conversations,
    error,
    fetchConversations,
    markConversationRead,
    selectConversation,
    deleteConversation,
    blockConversation,
  } = useMessages();

  const [activeFilter, setActiveFilter] = useState<MessageFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  // Skeletons own the list until the fetch for the current tab/search lands, so
  // the previous tab's rows are never shown as if they belonged to the new one.
  const [listLoading, setListLoading] = useState(true);
  const [menu, setMenu] = useState<{ conversation: Conversation; anchorRect: DOMRect } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Conversation | null>(null);
  const [blockTarget, setBlockTarget] = useState<Conversation | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  // Typing is debounced; switching tabs is not, so a tap fetches immediately.
  useEffect(() => {
    if (searchTerm === appliedSearch) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setAppliedSearch(searchTerm), 350);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchTerm, appliedSearch]);

  // Favourite / Groups are client-side views of the same payload, so only a
  // change of server filter or search term is worth another round trip.
  const serverFilter: 'All' | 'Buddies' = activeFilter === 'buddies' ? 'Buddies' : 'All';

  const loadConversations = useCallback(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setListLoading(true);

    fetchConversations({ refresh: true, searchTerm: appliedSearch, filter: serverFilter })
      .catch(() => undefined)
      .finally(() => {
        // A newer tab/search has already taken over — keep its skeletons up.
        if (requestIdRef.current === requestId) setListLoading(false);
      });
  }, [appliedSearch, serverFilter, fetchConversations]);

  useEffect(loadConversations, [loadConversations]);

  // Robi / Ask Anything threads live on their own screen — never in the inbox.
  const inboxConversations = useMemo(
    () =>
      conversations.filter(
        c => c.category !== 'ai' && !isAiConversationType(c.conversationType),
      ),
    [conversations],
  );

  const filtered = useMemo(() => {
    switch (activeFilter) {
      case 'buddies':
        return inboxConversations.filter(c => c.category !== 'groups');
      case 'favourite':
        return inboxConversations.filter(c => c.isFavourite);
      case 'groups':
        return inboxConversations.filter(
          c => c.category === 'groups' || isGroupConversationType(c.conversationType),
        );
      default:
        return inboxConversations;
    }
  }, [inboxConversations, activeFilter]);

  const filterChips = useMemo(() => [
    { key: 'all' as const, label: t('messagesScreen.all') },
    { key: 'buddies' as const, label: t('messagesScreen.buddies') },
    { key: 'groups' as const, label: t('messagesScreen.groups') },
  ], [t]);

  const handlePress = useCallback((conversation: Conversation) => {
    if (conversation.category === 'ai' || isAiConversationType(conversation.conversationType)) {
      navigate('/robi');
      return;
    }
    markConversationRead(conversation.id).catch(() => undefined);
    selectConversation(conversation.id);
    navigate(`/messages/${conversation.id}`);
  }, [markConversationRead, selectConversation, navigate]);

  const handleMenuSelect = useCallback((key: ConversationMenuKey) => {
    const target = menu?.conversation;
    setMenu(null);
    if (key === 'delete' && target) {
      setDeleteTarget(target);
    } else if (key === 'block' && target) {
      setBlockTarget(target);
    } else if (key === 'createGroup' && target) {
      const isGroup = isGroupConversationType(target.conversationType);
      if (!isGroup) {
        const initialMember = {
          id: target.peerUserId || String(target.id),
          name: target.name || t('chat.unknownUser'),
          avatar: target.avatar,
          locked: true,
        };
        navigate('/messages/group/new', { state: { initialMember } });
      }
    }
  }, [menu, t, navigate]);

  const menuItems = useMemo<PopoverMenuItem<ConversationMenuKey>[]>(() => {
    if (!menu?.conversation) return [];
    const isGroup = isGroupConversationType(menu.conversation.conversationType);
    const items: PopoverMenuItem<ConversationMenuKey>[] = [];
    if (!isGroup) {
      items.push({
        key: 'createGroup',
        label: t('messagesScreen.options.createGroup', { name: menu.conversation.name || t('chat.unknownUser') }),
        icon: PlusIcon,
      });
    }
    items.push(
      {
        key: 'block',
        label: t('messagesScreen.options.block'),
        icon: BlockIcon,
      },
      {
        key: 'delete',
        label: t('messagesScreen.options.delete'),
        icon: TrashIcon,
        destructive: true,
      },
    );
    return items;
  }, [menu, t]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await deleteConversation(deleteTarget.id);
      setDeleteTarget(null);
    } catch (deleteError) {
      ToastService.show({
        type: 'error',
        message:
          deleteError instanceof Error && deleteError.message
            ? deleteError.message
            : t('messagesScreen.errors.deleteFailed'),
      });
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleting, deleteConversation, t]);

  const confirmBlock = useCallback(async () => {
    if (!blockTarget || blocking) return;
    setBlocking(true);
    try {
      await blockConversation(blockTarget.id, blockTarget.peerUserId);
      setBlockTarget(null);
    } catch (blockError) {
      ToastService.show({
        type: 'error',
        message:
          blockError instanceof Error && blockError.message
            ? blockError.message
            : t('messagesScreen.errors.blockFailed'),
      });
    } finally {
      setBlocking(false);
    }
  }, [blockTarget, blocking, blockConversation, t]);

  const deleteTargetName = deleteTarget
    ? deleteTarget.groupName || deleteTarget.name || t('chat.unknownUser')
    : '';

  return (
    <section className="messages-page" aria-label={t('messagesScreen.messages')}>
      <div className="messages-search">
        <input
          className="messages-search__input"
          type="text"
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
          placeholder={t('messagesScreen.searchChats')}
          aria-label={t('messagesScreen.searchChats')}
        />
        <SearchIcon size={24} className="messages-search__icon" />
      </div>

      <div className="messages-filters" role="group" aria-label={t('messagesScreen.filters')}>
        {filterChips.map(chip => (
          <button
            key={chip.key}
            type="button"
            aria-pressed={activeFilter === chip.key}
            className={`messages-filter-chip${activeFilter === chip.key ? ' messages-filter-chip--active' : ''}`}
            onClick={() => setActiveFilter(chip.key)}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {listLoading ? (
        <ConversationSkeleton />
      ) : error && filtered.length === 0 ? (
        <ListStatus
          variant="error"
          message={error || t('messagesScreen.errors.loadFailed')}
          onAction={loadConversations}
          busy={listLoading}
        />
      ) : filtered.length === 0 ? (
        <ListStatus
          className="messages-list--enter"
          variant="empty"
          icon={<MessagesIcon size={48} />}
          title={t('messagesScreen.noConversations')}
          // Searching narrows the inbox, so only the unfiltered view offers a reload.
          onAction={appliedSearch ? undefined : loadConversations}
          busy={listLoading}
        />
      ) : (
        <div className="messages-list messages-list--enter" key={`${activeFilter}-${appliedSearch}`}>
          {filtered.map(conversation => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              onPress={handlePress}
              menuOpen={menu?.conversation.id === conversation.id}
              onMenu={(target, anchorRect) => setMenu({ conversation: target, anchorRect })}
            />
          ))}
        </div>
      )}

      {menu && (
        <PopoverMenu
          anchorRect={menu.anchorRect}
          items={menuItems}
          label={t('messagesScreen.accessibility.conversationMenu')}
          labelledBy="conversation-menu-title"
          onClose={() => setMenu(null)}
          onSelect={handleMenuSelect}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('messagesScreen.deleteChatTitle')}
          message={t('messagesScreen.deleteChatDescription', { name: deleteTargetName })}
          confirmLabel={t('common.buttons.delete')}
          cancelLabel={t('common.buttons.cancel')}
          mediaUrl={resolveAvatarSrc(deleteTarget.avatar)}
          loading={deleting}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => { confirmDelete().catch(() => undefined); }}
          showIcon
        />
      )}

      {blockTarget && (
        <ConfirmModal
          title={t('messagesScreen.blockTitle')}
          message={t('messagesScreen.blockDescription')}
          confirmLabel={t('messagesScreen.blockConfirm')}
          cancelLabel={t('common.buttons.cancel')}
          mediaUrl={resolveAvatarSrc(blockTarget.avatar)}
          loading={blocking}
          onClose={() => setBlockTarget(null)}
          onConfirm={() => { confirmBlock().catch(() => undefined); }}
          showIcon
        />
      )}
    </section>
  );
}
